<script setup lang="ts">
import { TriangleAlert } from '@lucide/vue'
import {
    Button,
    Checkbox,
    Input,
    Label,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
    ZhSystemLabel,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { api, type Me } from '@/api'
import {
    type AccessChangeRow,
    type EnterpriseUserRow,
    type IssuedInvitation,
    type OutstandingInvitationRow,
    UsersApiError,
    usersApi,
} from '@/api-users'
import CopyButton from '@/components/CopyButton.vue'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import GrantPresetPicker from '@/components/GrantPresetPicker.vue'
import MailAliasesPanel from '@/components/MailAliasesPanel.vue'
import PersonProfileForm from '@/components/PersonProfileForm.vue'
import ScopeLevelPicker from '@/components/ScopeLevelPicker.vue'
import { useClientTable } from '@/composables/useClientTable'
import { everyResourceAt, type GrantPresetKey } from '@/composables/useGrantPresets'
import { useListState } from '@/composables/useListState'
import { LEVEL_RANK, type ScopeLevel, useScopeLevels } from '@/composables/useScopeLevels'
import { type ScopeChip, scopeChips } from '@/lib/scopeChips'

/**
 * #618: who is in this enterprise and what they hold. Deliberately not the Members screen, which is the AI
 * org chart — these are the people who can reach the company's data, which is a different question.
 *
 * #626 adds the other half of the same question: who has been *invited* and has not joined yet. An
 * invitation is a live credential — whoever holds the link can join at the grant it carries — so an owner
 * reads the outstanding ones here, beside the people already in, and can withdraw one on the spot.
 *
 * #619 adds sending one: anybody holding `users:write` names who they are inviting and what they may reach,
 * with the same per-resource picker the API tokens screen uses, capped server-side at their own grant.
 *
 * #628 makes the common cases quick: three named starting points (Watching, Operating, Full access) above
 * that picker, so adding a co-founder is one action rather than twenty, and none of them ever proposes more
 * than the inviter holds themself. A preset is a starting point, not a cage — every resource stays settable
 * after one is chosen, and anybody who would rather set all twenty by hand still has the picker that was
 * always there.
 *
 * #629 puts each person's access history on their own entry — no screen of its own: who invited them, changed
 * their grant or removed them, what they held before and after, and when. It is read only when an owner opens
 * it, one person at a time.
 */

/** A user row plus the strings the table searches and sorts on, none of them a raw scope. */
type UserRow = EnterpriseUserRow & { chips: ScopeChip[]; grantText: string; joinedLabel: string }

/** One outstanding invitation with every part of it already said in words: who, what, when, from whom. */
type InvitationRow = OutstandingInvitationRow & {
    forLabel: string
    grantLabel: string
    /** What a member invitation would grant, as chips; empty for an owner invitation or one granting nothing. */
    chips: ScopeChip[]
    lapsesLabel: string
    sentByLabel: string
}

/**
 * What an invitation would grant, in the same words the people table uses for the same thing: the stored
 * grant is an `EnterpriseHuman` role (`ceo` for owning the enterprise outright), and nobody should have to
 * know that to read the row.
 */
const GRANT_LABELS: Record<string, string> = { ceo: 'Owner', member: 'Member' }

const users = ref<EnterpriseUserRow[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

const invitations = ref<OutstandingInvitationRow[]>([])
const invitationsLoading = ref(true)
const invitationsError = ref<string | null>(null)
/** The invitation currently being withdrawn, so only its own button goes quiet while the write is in flight. */
const withdrawing = ref<string | null>(null)

function titleCase(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1)
}

function chipsFor(user: EnterpriseUserRow): ScopeChip[] {
    // Owning is the whole grant, so an owner has nothing to enumerate and reads as "Owner" instead.
    return user.isOwner ? [] : scopeChips(user.scopes)
}

/**
 * A date string as a `Date`, or null for anything that cannot be read as one — missing or otherwise. Shared by
 * every date column on this screen so each one only has to say how it formats a valid date, not re-check what
 * counts as one.
 */
function parseDate(value: string | null): Date | null {
    if (!value) {
        return null
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

/** A date a person can read. Fixed to UTC so the same membership never reads as two different days. */
function joinedLabel(joinedAt: string | null): string {
    const date = parseDate(joinedAt)
    if (!date) {
        return '—'
    }
    return date.toLocaleDateString('en-US', { timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric' })
}

const rows = computed<UserRow[]>(() =>
    users.value.map(user => {
        const chips = chipsFor(user)
        return {
            ...user,
            chips,
            // Search reads the same words the screen shows, never the raw scope behind them.
            grantText: user.isOwner ? 'Owner' : chips.map(chip => chip.label).join(', '),
            joinedLabel: joinedLabel(user.joinedAt),
        }
    }),
)

/**
 * A moment to the minute and in UTC: when an invitation lapses, or when somebody's access changed (#629). Said
 * to the minute on purpose: a date alone cannot tell an owner whether a link they are looking at dies before
 * the end of today, which is the whole reason they are reading that column.
 */
function minuteLabel(moment: string): string {
    const date = parseDate(moment)
    if (!date) {
        return '—'
    }
    const said = date.toLocaleString('en-US', {
        timeZone: 'UTC',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })
    return `${said} UTC`
}

const invitationRows = computed<InvitationRow[]>(() =>
    invitations.value.map(invitation => ({
        ...invitation,
        // An invitation is bound to whoever holds the link, not to the address it was sent to, so a row with
        // no address says so rather than showing an empty cell somebody would read as a missing name.
        forLabel: invitation.email ?? 'Whoever holds the link',
        grantLabel: GRANT_LABELS[invitation.grant] ?? titleCase(invitation.grant),
        chips: invitation.grant === 'ceo' ? [] : scopeChips(invitation.scopes),
        lapsesLabel: minuteLabel(invitation.expiresAt),
        sentByLabel: invitation.invitedByName ?? '—',
    })),
)

/** One access change with every part of it already said in words: what happened, by whom, from what to what, when. */
type AccessChangeView = { id: string; what: string; by: string; before: string; after: string; when: string }

/** What each kind of change reads as. Ownership moving is said by which way it went, in `changeWording`. */
const CHANGE_LABELS: Record<string, string> = {
    invitation_sent: 'Invited',
    invitation_accepted: 'Accepted an invitation',
    invitation_withdrawn: 'Invitation withdrawn',
    grant_changed: 'Grant changed',
    member_removed: 'Removed from this enterprise',
}

function changeWording(change: AccessChangeRow): string {
    if (change.action === 'ownership_moved') {
        return change.after?.isOwner ? 'Made an owner' : 'No longer an owner'
    }
    return CHANGE_LABELS[change.action] ?? titleCase(change.action.replace(/_/g, ' '))
}

/**
 * A grant either side of a change, in the words the people table uses for the same thing — never a raw scope.
 * Null is somebody who held nothing here at all: not yet in the enterprise, or no longer.
 */
function grantWords(grant: AccessChangeRow['before']): string {
    if (!grant) {
        return 'Not in this enterprise'
    }
    if (grant.isOwner) {
        return 'Owner'
    }
    const chips = scopeChips(grant.scopes)
    return chips.length > 0 ? chips.map(chip => chip.label).join(', ') : 'No grant'
}

/**
 * Whose public profile is open for editing, if anybody's (#1266): one person at a time, on their own entry. The
 * form is presentation only, so opening or saving it never touches what that person holds here.
 */
const profileFor = ref<EnterpriseUserRow | null>(null)

function toggleProfile(person: EnterpriseUserRow): void {
    profileFor.value = profileFor.value?.id === person.id ? null : person
}

/**
 * #882: whose mailbox address is being given or changed, if anybody's: one person at a time, on their own entry.
 * The draft starts as the address they hold, so moving somebody is an edit, not a retype. The server judges the
 * address again whatever is drawn here; this only says its refusal in words.
 */
const mailboxFor = ref<string | null>(null)
const mailboxDraft = ref('')
const mailboxBusy = ref(false)
const mailboxError = ref<string | null>(null)

function toggleMailbox(person: EnterpriseUserRow): void {
    if (mailboxFor.value === person.id) {
        mailboxFor.value = null
        return
    }
    mailboxFor.value = person.id
    mailboxDraft.value = person.mailAddress ?? ''
    mailboxError.value = null
}

/** The row as the API now has it, in place, so the entry reads right without reloading everybody. */
function replaceRow(updated: EnterpriseUserRow): void {
    users.value = users.value.map(user => (user.id === updated.id ? updated : user))
}

/** A refusal in words a person can act on: the API's code, never its status, decides which. */
function mailboxWording(cause: unknown): string {
    switch (cause instanceof Error ? cause.message : '') {
        case 'mailbox_address_invalid':
            return 'Give one address as name@domain: letters, digits and dots, nothing else.'
        case 'mailbox_domain_unavailable':
            return 'This enterprise does not receive mail at that domain. Attach the domain and prove you own it first.'
        case 'mailbox_address_taken':
            return 'That address already delivers to a teammate or to another person.'
        case 'not_enterprise_owner':
            return 'Only an owner can give someone a mailbox address.'
        default:
            return 'Could not save the address. Try again.'
    }
}

async function saveMailbox(person: EnterpriseUserRow): Promise<void> {
    mailboxBusy.value = true
    mailboxError.value = null
    try {
        replaceRow(await usersApi.setMailbox(person.id, mailboxDraft.value))
        mailboxFor.value = null
    } catch (cause) {
        mailboxError.value = mailboxWording(cause)
    } finally {
        mailboxBusy.value = false
    }
}

async function removeMailbox(person: EnterpriseUserRow): Promise<void> {
    mailboxBusy.value = true
    mailboxError.value = null
    try {
        replaceRow(await usersApi.clearMailbox(person.id))
        mailboxFor.value = null
    } catch (cause) {
        mailboxError.value = mailboxWording(cause)
    } finally {
        mailboxBusy.value = false
    }
}

/** Whose access history is open, if anybody's: one person at a time, read when their entry is opened. */
const historyFor = ref<string | null>(null)
const history = ref<AccessChangeRow[]>([])
const historyLoading = ref(false)
const historyError = ref<string | null>(null)

/** The open history, newest first as the API sent it, said in words. */
const historyRows = computed<AccessChangeView[]>(() =>
    history.value.map(change => ({
        id: change.id,
        what: changeWording(change),
        by: change.actorName ?? 'somebody no longer on record',
        before: grantWords(change.before),
        after: grantWords(change.after),
        when: minuteLabel(change.createdAt),
    })),
)

/**
 * Opens one person's access history on their own entry, or closes it if it is already open. Only that
 * person's history is read, and only now: nobody's is fetched for a screen that never asks for it.
 */
async function toggleHistory(id: string): Promise<void> {
    if (historyFor.value === id) {
        historyFor.value = null
        return
    }
    historyFor.value = id
    history.value = []
    historyError.value = null
    historyLoading.value = true
    let changes: AccessChangeRow[] = []
    let failure: string | null = null
    try {
        changes = await usersApi.history(id)
    } catch (cause) {
        failure = refusal(cause)
            ? 'You do not have permission to see this person’s access history. Ask an owner.'
            : 'Could not load this person’s access history.'
    }
    // Somebody else's entry may have been opened while this was in flight; theirs is the one on screen now.
    if (historyFor.value !== id) {
        return
    }
    history.value = changes
    historyError.value = failure
    historyLoading.value = false
}

const table = useClientTable({
    data: rows,
    searchFields: ['name', 'grantText'],
    defaultSort: 'name',
    defaultOrder: 'ASC',
})
/** #633: the table's search, sort and filters come back on the next visit. */
const list = useListState('users', table.listFields)

const columns: DataTableColumn[] = [
    { key: 'name', label: 'Person', sort: 'name', type: 'text' },
    { key: 'grantText', label: 'Grant', sort: 'grantText', type: 'text' },
    { key: 'joinedLabel', label: 'Joined', sort: 'joinedAt', filterable: false },
]

/**
 * The signed-in person's own grant (#619), which decides whether this screen offers to invite anybody and
 * whether owning the enterprise is one of the grants offered. The server decides both again whatever is drawn
 * here: this only keeps the screen from offering what it would refuse.
 */
const me = ref<Me | null>(null)

const canInvite = computed(() => {
    const grant = me.value
    if (!grant) {
        return false
    }
    // An API older than #622 reports no grant at all, and the portal restricts nothing then, as the nav does.
    if (grant.isOwner === undefined && grant.scopes === undefined) {
        return true
    }
    return grant.isOwner === true || (grant.scopes ?? []).includes('users:write')
})
const mayInviteOwner = computed(() => me.value?.isOwner === true)

/**
 * What the signed-in person holds, as per-resource levels, so a preset never proposes more than that (#628).
 *
 * An owner's own grant enumerates nothing (`scopes` is null), and an API older than #622 reports no grant at
 * all — the portal restricts nothing in that second case, as the nav does, and reads the inviter as holding
 * everything rather than capping every preset at `none`. Owning is handled separately, by `mayInviteOwner`:
 * the picker skips clamping altogether for an owner. The server caps the grant again either way.
 */
const heldLevels = computed<Record<string, ScopeLevel>>(() => {
    const grant = me.value
    const unknownGrant = !grant || (grant.isOwner === undefined && grant.scopes === undefined)
    const levels = everyResourceAt(unknownGrant ? 'write' : 'none')
    for (const scope of grant?.scopes ?? []) {
        const [resource = '', level = ''] = scope.split(':')
        // A grant listing both `read` and `write` on one resource holds the wider of the two.
        const known = level === 'read' || level === 'write'
        if (known && resource in levels && LEVEL_RANK[level] > LEVEL_RANK[levels[resource] ?? 'none']) {
            levels[resource] = level
        }
    }
    return levels
})

const inviteEmail = ref('')
const inviteAsOwner = ref(false)
/**
 * Which preset started this grant, if any (#628). Null means nobody chose one, and the invite form then
 * shows the plain per-resource picker it always had; once a preset has started a grant, the preset picker
 * owns the grid instead, so it can fill it in one action and say when it has since been adjusted. Nothing is
 * sent to the API about it: the patch is the same `{ isOwner, scopes }` it has always been.
 */
const chosenPreset = ref<GrantPresetKey | null>(null)
/**
 * The preset picker itself, so this form can tell it to forget what it applied when a grant starts over. The
 * picker is mounted once for the life of this screen, and keeps which preset produced the grant on screen:
 * resetting `chosenPreset` here alone would leave the next invitation showing the last one's "customized"
 * indicator and highlighted button.
 */
const presetPicker = ref<InstanceType<typeof GrantPresetPicker> | null>(null)
const { levels: inviteLevels, scopes: inviteScopes } = useScopeLevels()
const inviting = ref(false)
const inviteError = ref<string | null>(null)
/** The one moment an invitation's link exists in the portal. Cleared as soon as the person dismisses it. */
const issued = ref<IssuedInvitation | null>(null)

onMounted(() => {
    // Every list is asked for at once rather than one after the other: they are endpoints answering one
    // screen, and none is waiting on anything another learns.
    void loadUsers()
    void loadInvitations()
    void api
        .me()
        .then(session => {
            me.value = session
        })
        .catch(() => undefined)
})

/**
 * Ticks or unticks "make them an owner" by hand, and forgets any preset that hand contradicts (#628).
 *
 * Ownership is a grant in its own right: Full access *is* ownership and enumerates nothing, while every other
 * preset is explicitly not ownership. So turning this checkbox the other way from what the chosen preset
 * produced undoes that preset, and the screen must stop claiming it. The preset picker cannot see this
 * checkbox — it only reads levels — and its drift indicator compares levels alone, so unticking ownership
 * after Full access moves no level and can never read as drift: without this, "Full access" would stay
 * highlighted, with no "customized" beside it, over an invitation that now grants nothing at all.
 *
 * Forgetting the preset rather than flagging drift is what the rest of this screen already does with an
 * unstarted grant: no preset chosen means the by-hand picker comes back, holding whatever levels are actually
 * there, which is the honest account of a grant nobody applied a preset to any more.
 */
function setInviteAsOwner(asOwner: boolean): void {
    inviteAsOwner.value = asOwner
    if (chosenPreset.value !== null && asOwner !== (chosenPreset.value === 'full-access')) {
        chosenPreset.value = null
        presetPicker.value?.reset()
    }
}

/**
 * Sends one invitation and shows its link, once. The form clears on success so a second invitation starts
 * from nothing chosen; the outstanding list is re-read where this person may read it, so the new one appears.
 */
async function invite(): Promise<void> {
    inviting.value = true
    inviteError.value = null
    const asOwner = mayInviteOwner.value && inviteAsOwner.value
    try {
        issued.value = await usersApi.invite({
            email: inviteEmail.value.trim(),
            isOwner: asOwner,
            scopes: asOwner ? [] : inviteScopes.value,
        })
        inviteEmail.value = ''
        inviteAsOwner.value = false
        chosenPreset.value = null
        inviteLevels.value = Object.fromEntries(Object.keys(inviteLevels.value).map(resource => [resource, 'none']))
        // And the picker's own memory of which preset produced that grant, which nothing else here can reach:
        // it is never remounted, so an unsaid reset would carry the last invitation's preset into the next one.
        presetPicker.value?.reset()
        if (!error.value) {
            void loadInvitations()
        }
    } catch (cause) {
        inviteError.value = inviteRefusal(cause)
    } finally {
        inviting.value = false
    }
}

/**
 * Why an invitation was refused, in the API's own sentence where it gave one: a grant reaching too far is
 * refused naming exactly what overreached, which is more use to the sender than any summary of it.
 */
function inviteRefusal(cause: unknown): string {
    if (cause instanceof UsersApiError && cause.detail) {
        return cause.detail
    }
    return refusal(cause)
        ? 'You do not have permission to invite anybody. Ask an owner.'
        : 'Could not send that invitation.'
}

/** What happened to the email carrying the link, so nobody assumes it arrived when it did not. */
function mailLabel(invitation: IssuedInvitation): string {
    if (invitation.mail === 'sent') {
        return `Emailed to ${invitation.email}.`
    }
    if (invitation.mail === 'failed') {
        return `The email to ${invitation.email} did not go out — send them this link yourself.`
    }
    return 'No email was sent — send them this link yourself.'
}

async function loadUsers(): Promise<void> {
    try {
        users.value = await usersApi.users()
    } catch (cause) {
        // A refused caller is told why and shown nothing: half a table would leak the very rows the API
        // just withheld.
        error.value = refusal(cause)
            ? 'You do not have permission to see who is in this enterprise. Ask an owner.'
            : 'Could not load the people in this enterprise.'
    } finally {
        loading.value = false
    }
}

/**
 * The outstanding invitations, kept apart from the people list's own failure: one list failing is no reason
 * to withhold the other, and both are owner-gated, so a refusal here is explained in its own section rather
 * than blanking the screen.
 */
async function loadInvitations(): Promise<void> {
    try {
        invitations.value = await usersApi.invitations()
    } catch (cause) {
        invitationsError.value = refusal(cause)
            ? 'You do not have permission to see outstanding invitations. Ask an owner.'
            : 'Could not load outstanding invitations.'
    } finally {
        invitationsLoading.value = false
    }
}

/**
 * Withdraws one invitation, after asking: the link stops working on the very next attempt to follow it, and
 * there is no undo — a withdrawn invitation has to be sent again.
 *
 * `invitation_not_found` is not reported as a failure but as the row already being gone: it means somebody
 * else withdrew or accepted it since this list was read, and the honest thing is to drop it from the list
 * the same way a successful withdrawal does.
 */
async function withdraw(row: InvitationRow): Promise<void> {
    if (!confirm(`Withdraw the invitation for ${row.forLabel}? The link stops working immediately.`)) {
        return
    }
    withdrawing.value = row.id
    invitationsError.value = null
    try {
        await usersApi.withdrawInvitation(row.id)
        invitations.value = invitations.value.filter(invitation => invitation.id !== row.id)
    } catch (cause) {
        const code = errorCode(cause)
        if (code === 'invitation_not_found' || code === '404') {
            invitations.value = invitations.value.filter(invitation => invitation.id !== row.id)
            invitationsError.value = 'That invitation was already gone — somebody else withdrew or accepted it.'
        } else {
            invitationsError.value = refusal(cause)
                ? 'You do not have permission to withdraw an invitation. Ask an owner.'
                : 'Could not withdraw that invitation.'
        }
    } finally {
        withdrawing.value = null
    }
}

/** Whatever a caught failure says, as a plain string: an `Error`'s own message, or the thrown value itself. */
function errorCode(cause: unknown): string {
    return cause instanceof Error ? cause.message : String(cause)
}

function refusal(cause: unknown): boolean {
    const code = errorCode(cause)
    return code === '403' || code === 'not_enterprise_owner'
}
</script>

<template>
	<section class="w-full space-y-4 p-8">
		<ZhPageTitle>People</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			Everyone who can reach this enterprise, what each of them holds, and when they joined.
		</p>
		<p v-if="error" class="text-destructive text-sm" data-testid="users-error">{{ error }}</p>
		<!-- The people, on a kit surface of their own; the table draws its own cell padding, so the panel is flush. -->
		<ZhPanel v-else flush>
			<DataTable
				storage-key="users"
				:columns="columns"
				:loading="loading"
				:list-state="list"
				v-bind="table.tableProps.value"
			>
				<template #cell-name="{ row }">
					<div class="space-y-2">
						<span class="flex items-center gap-2">
							<!-- Square, as every brand surface is: the kit draws no rounded lockup (BG §17.2). -->
							<img
								v-if="(row as unknown as UserRow).avatarUrl"
								:src="(row as unknown as UserRow).avatarUrl ?? ''"
								alt=""
								class="size-8"
							/>
							<span class="font-medium">{{ row.name }}</span>
							<!-- Owning the enterprise is said outright, never left to be inferred from an empty grant. -->
							<ZhSystemLabel
								v-if="(row as unknown as UserRow).isOwner"
								as="span"
								tone="system"
								data-testid="owner-badge"
							>
								Owner
							</ZhSystemLabel>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								:data-testid="`show-history-${(row as unknown as UserRow).id}`"
								:aria-expanded="historyFor === (row as unknown as UserRow).id"
								@click.stop="toggleHistory((row as unknown as UserRow).id)"
							>
								History
							</Button>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								data-testid="edit-profile"
								:aria-expanded="profileFor?.id === (row as unknown as UserRow).id"
								@click.stop="toggleProfile(row as unknown as UserRow)"
							>
								Profile
							</Button>
						</span>
						<!-- #882: the person's own mailbox address, given by an owner; mail to it is kept for them alone. -->
						<p
							class="text-muted-foreground flex flex-wrap items-center gap-2 text-sm"
							:data-testid="`mailbox-${(row as unknown as UserRow).id}`"
						>
							<template v-if="(row as unknown as UserRow).mailAddress">
								<span>Mail:</span>
								<span class="font-mono" data-testid="mailbox-address">
									{{ (row as unknown as UserRow).mailAddress }}
								</span>
							</template>
							<span v-else>No mailbox address</span>
							<Button
								type="button"
								size="sm"
								variant="ghost"
								:data-testid="`edit-mailbox-${(row as unknown as UserRow).id}`"
								:aria-expanded="mailboxFor === (row as unknown as UserRow).id"
								@click.stop="toggleMailbox(row as unknown as UserRow)"
							>
								{{ (row as unknown as UserRow).mailAddress ? 'Change' : 'Give one' }}
							</Button>
						</p>
						<ZhPanel
							v-if="mailboxFor === (row as unknown as UserRow).id"
							as="div"
							:data-testid="`mailbox-panel-${(row as unknown as UserRow).id}`"
							class="space-y-2"
						>
							<form
								class="flex flex-wrap items-end gap-2"
								data-testid="mailbox-form"
								@submit.prevent="saveMailbox(row as unknown as UserRow)"
							>
								<div class="space-y-1">
									<Label :for="`mailbox-address-${(row as unknown as UserRow).id}`">Address</Label>
									<Input
										:id="`mailbox-address-${(row as unknown as UserRow).id}`"
										v-model="mailboxDraft"
										type="email"
										placeholder="name@yourdomain.com"
										class="w-72"
										:data-testid="`mailbox-input-${(row as unknown as UserRow).id}`"
									/>
								</div>
								<Button
									type="submit"
									size="sm"
									:disabled="mailboxBusy"
									:data-testid="`save-mailbox-${(row as unknown as UserRow).id}`"
								>
									Save
								</Button>
								<Button
									v-if="(row as unknown as UserRow).mailAddress"
									type="button"
									size="sm"
									variant="outline"
									:disabled="mailboxBusy"
									:data-testid="`remove-mailbox-${(row as unknown as UserRow).id}`"
									@click="removeMailbox(row as unknown as UserRow)"
								>
									Withdraw
								</Button>
							</form>
							<p class="text-muted-foreground text-xs">
								One address at a domain this enterprise receives mail on. Mail sent to it is kept for
								{{ row.name }} alone; nobody else reads it. Withdrawing it stops delivery; what was kept
								stays theirs.
							</p>
							<!-- #886: the person's extra addresses, delivering to the same mailbox, managed where the address is. -->
							<MailAliasesPanel :person-id="(row as unknown as UserRow).id" editable />
							<p
								v-if="mailboxError"
								class="text-destructive text-sm"
								role="alert"
								data-testid="mailbox-error"
							>
								{{ mailboxError }}
							</p>
						</ZhPanel>
						<!-- #1266: this person's public profile, on their own entry, edited apart from their grant. -->
						<ZhPanel
							v-if="profileFor && profileFor.id === (row as unknown as UserRow).id"
							as="div"
							:data-testid="`profile-panel-${(row as unknown as UserRow).id}`"
						>
							<PersonProfileForm :person="profileFor" @saved="profileFor = null" />
						</ZhPanel>
						<!-- #629: this person's access history, on their own entry, read only once it is opened. -->
						<ZhPanel
							v-if="historyFor === (row as unknown as UserRow).id"
							as="div"
							:data-testid="`access-history-${(row as unknown as UserRow).id}`"
							class="space-y-2 text-sm"
						>
							<p v-if="historyLoading" class="text-muted-foreground">Loading…</p>
							<p v-else-if="historyError" class="text-destructive" data-testid="access-history-error">
								{{ historyError }}
							</p>
							<ZhEmptyState
								v-else-if="historyRows.length === 0"
								label="No changes recorded"
								data-testid="no-access-history"
							>
								No change to their access has been recorded yet.
							</ZhEmptyState>
							<ol v-else class="space-y-2">
								<li v-for="change in historyRows" :key="change.id" data-testid="access-change">
									<p>
										<span class="font-medium">{{ change.what }}</span>
										by {{ change.by }}
										<span class="text-muted-foreground">· {{ change.when }}</span>
									</p>
									<p class="text-muted-foreground">{{ change.before }} → {{ change.after }}</p>
								</li>
							</ol>
						</ZhPanel>
					</div>
				</template>
				<!-- The grant reads as chips; `grantText` stays the string search and sort use. -->
				<template #cell-grantText="{ row }">
					<span v-if="(row as unknown as UserRow).isOwner" class="text-muted-foreground">Owner</span>
					<span v-else-if="(row as unknown as UserRow).chips.length" class="flex flex-wrap gap-1">
						<ZhSystemLabel
							v-for="chip in (row as unknown as UserRow).chips"
							:key="chip.key"
							as="span"
							data-testid="scope-chip"
						>
							{{ chip.label }}
						</ZhSystemLabel>
					</span>
					<span v-else class="text-muted-foreground">No grant</span>
				</template>
			</DataTable>
		</ZhPanel>

		<!--
			#619: inviting somebody. Offered to whoever may (an owner, or `users:write`), whether or not they may
			read the lists around it, and never offering a grant wider than the server would accept from them.
		-->
		<ZhPanel v-if="canInvite" class="space-y-3" aria-labelledby="invite-somebody">
			<ZhSectionHeading id="invite-somebody">Invite somebody</ZhSectionHeading>
			<p class="text-muted-foreground max-w-2xl text-sm">
				Choose what they may reach before anything is sent. They are emailed a link that signs them in with
				GitHub; it works once and lapses after seven days. You can only grant what you hold yourself.
			</p>
			<!-- The one-time link, on a surface of its own so it cannot be read as part of the next invitation. -->
			<ZhPanel v-if="issued" as="div" class="space-y-2" data-testid="issued-invitation">
				<p class="flex items-center gap-2 text-sm font-medium">
					<TriangleAlert class="size-4" />
					Copy this link now — it is never shown again.
				</p>
				<div class="flex items-center gap-2">
					<code class="grow overflow-x-auto font-mono text-xs" data-testid="invitation-link">{{ issued.link }}</code>
					<CopyButton :value="issued.link" />
				</div>
				<p class="text-muted-foreground text-sm" data-testid="invitation-mail">{{ mailLabel(issued) }}</p>
				<Button size="sm" variant="outline" data-testid="dismiss-invitation" @click="issued = null">Done</Button>
			</ZhPanel>
			<p v-if="inviteError" class="text-destructive text-sm" data-testid="invite-error">{{ inviteError }}</p>
			<form class="space-y-4" data-testid="invite-form" @submit.prevent="invite">
				<label class="block text-sm">
					Email
					<Input
						v-model="inviteEmail"
						type="email"
						required
						maxlength="320"
						placeholder="name@example.com"
						data-testid="invite-email"
						class="mt-1 w-72"
					/>
				</label>
				<!--
					Bound by hand rather than `v-model`: ticking or unticking ownership can contradict the
					preset that produced the grant on screen (Full access is ownership itself), and only the
					form can tell the picker to forget a preset that no longer describes anything.
					#1277: the kit Checkbox is a `role="checkbox"` button, which a wrapping <label> does not
					name, so the kit Label points at it by id. No `name`: that would add reka-ui's hidden form
					input, a second checkbox the form never reads.
				-->
				<div v-if="mayInviteOwner" class="flex items-center gap-2">
					<Checkbox
						id="invite-owner-control"
						:model-value="inviteAsOwner"
						data-testid="invite-owner"
						@update:model-value="setInviteAsOwner($event === true)"
					/>
					<Label for="invite-owner-control">Make them an owner, with the run of the whole enterprise</Label>
				</div>
				<!--
					#628: the quick path, offered before the twenty separate decisions. The presets stay on
					screen whatever is chosen, so a grant can be started over from a narrower one; Full
					access is ownership itself, which is why it sets the owner box above rather than
					enumerating anything.
				-->
				<GrantPresetPicker
					ref="presetPicker"
					:levels="inviteLevels"
					:held-levels="heldLevels"
					:held-is-owner="mayInviteOwner"
					:show-levels="chosenPreset !== null && !inviteAsOwner"
					@update:levels="inviteLevels = $event"
					@update:is-owner="inviteAsOwner = $event"
					@update:preset="chosenPreset = $event"
				/>
				<!--
					The picker #619 built, still the way to set every resource by hand from nothing. Once a
					preset has started the grant, the preset picker draws the same twenty resources itself,
					with the preset it drifted from named beside them, so only one grid is ever on screen.
				-->
				<ScopeLevelPicker
					v-if="!inviteAsOwner && chosenPreset === null"
					v-model="inviteLevels"
					testid-prefix="invite-scope-"
				/>
				<Button type="submit" size="sm" data-testid="send-invitation" :disabled="inviting || !inviteEmail.trim()">
					Invite
				</Button>
			</form>
		</ZhPanel>

		<!--
			#626: the other half of who can reach this enterprise. Hidden entirely when the people list was
			refused, because the same ownership gates both: a caller who may not see who is in may not see who
			has been invited either, and rendering an empty section would read as "nobody is waiting".
		-->
		<ZhPanel v-if="!error" class="space-y-3" aria-labelledby="outstanding-invitations">
			<!-- The heading reads from its slot, not a `title` prop: the prop is silently dropped. -->
			<ZhSectionHeading id="outstanding-invitations">Outstanding invitations</ZhSectionHeading>
			<p class="text-muted-foreground text-sm">
				Invitations that have been sent but not accepted yet. Anyone holding one of these links can join at
				the grant it carries, so withdraw any you did not mean to send — the link stops working immediately.
			</p>
			<p v-if="invitationsError" class="text-destructive text-sm" data-testid="invitations-error">
				{{ invitationsError }}
			</p>
			<p v-if="invitationsLoading" class="text-muted-foreground text-sm" data-testid="invitations-loading">
				Loading…
			</p>
			<ZhEmptyState
				v-else-if="invitationRows.length === 0 && !invitationsError"
				label="No invitations"
				data-testid="no-invitations"
			>
				Nobody is waiting on an invitation.
			</ZhEmptyState>
			<Table v-else-if="invitationRows.length" data-testid="invitations-table">
				<TableHeader>
					<TableRow>
						<TableHead>Invited</TableHead>
						<TableHead>Grant</TableHead>
						<TableHead>Lapses</TableHead>
						<TableHead>Sent by</TableHead>
						<TableHead></TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					<TableRow v-for="row in invitationRows" :key="row.id" :data-testid="`invitation-${row.id}`">
						<TableCell>{{ row.forLabel }}</TableCell>
						<TableCell>
							<!-- A member invitation reads as the scopes it would grant, in the people table's words. -->
							<span v-if="row.chips.length" class="flex flex-wrap gap-1">
								<ZhSystemLabel
									v-for="chip in row.chips"
									:key="chip.key"
									as="span"
									data-testid="invitation-scope-chip"
								>
									{{ chip.label }}
								</ZhSystemLabel>
							</span>
							<ZhSystemLabel v-else as="span" data-testid="invitation-grant">{{ row.grantLabel }}</ZhSystemLabel>
						</TableCell>
						<TableCell data-testid="invitation-lapses">{{ row.lapsesLabel }}</TableCell>
						<TableCell data-testid="invitation-sent-by">{{ row.sentByLabel }}</TableCell>
						<TableCell class="text-right">
							<Button
								size="sm"
								variant="outline"
								:data-testid="`withdraw-${row.id}`"
								:disabled="withdrawing === row.id"
								@click="withdraw(row)"
							>
								Withdraw
							</Button>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</ZhPanel>
	</section>
</template>
